<template>
  <nav class="dsn-nav bg-white/80 dark:bg-black/60 backdrop-blur-xl border-b border-black/[0.06] dark:border-white/[0.06]">
    <!-- Sliding pill indicator -->
    <span
      class="dsn-indicator"
      :style="indicatorStyle"
    />
    <button
      v-for="(tab, index) in tabs"
      :key="tab.key"
      :ref="(el) => setTabRef(el as HTMLElement | null, index)"
      class="dsn-tab flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer select-none transition-colors duration-150 border-none bg-transparent"
      :class="{ 'dsn-tab--active': modelValue === tab.key }"
      @click="$emit('update:modelValue', tab.key)"
    >
      <component
        :is="tab.icon"
        class="size-4 shrink-0 text-muted-foreground opacity-70"
        :stroke-width="1.9"
      />
      <span class="text-[13px] font-medium text-muted-foreground tracking-tight whitespace-nowrap">
        {{ tab.label }}
      </span>
    </button>

    <!-- Teleport target for period navigation -->
    <div id="topbar-period-tabs" class="ml-auto flex items-center shrink-0" />
  </nav>
</template>

<script setup lang="ts">
defineOptions({ name: 'DesktopSubnav' })

import { ref, watch, nextTick, onMounted, type Component, type CSSProperties } from 'vue'

export interface SubnavTab {
  key: string
  label: string
  icon: Component
}

const props = defineProps<{
  tabs: SubnavTab[]
  modelValue: string
}>()

defineEmits<{
  'update:modelValue': [value: string]
}>()

// ── Sliding indicator ──
const tabRefs = new Map<number, HTMLElement>()

const setTabRef = (el: HTMLElement | null, index: number) => {
  if (el) tabRefs.set(index, el)
  else tabRefs.delete(index)
}

const indicatorStyle = ref<CSSProperties>({
  opacity: '0',
  transform: 'translate(0, 0)',
  width: '0px',
})

// Track current indicator position for stretch animation
let currentLeft = 0
let currentWidth = 0
let settleTimer = 0

const getTabRect = (index: number) => {
  const el = tabRefs.get(index)
  if (!el) return null
  const navEl = el.closest('.dsn-nav') as HTMLElement | null
  if (!navEl) return null
  const navRect = navEl.getBoundingClientRect()
  const tabRect = el.getBoundingClientRect()
  return {
    left: tabRect.left - navRect.left,
    top: tabRect.top - navRect.top,
    width: tabRect.width,
    height: tabRect.height,
  }
}

const setIndicator = (left: number, top: number, width: number, height: number) => {
  indicatorStyle.value = {
    opacity: '1',
    transform: `translate(${left}px, ${top}px)`,
    width: `${width}px`,
    height: `${height}px`,
  }
}

const updateIndicator = (animate: boolean) => {
  const activeIndex = props.tabs.findIndex(t => t.key === props.modelValue)
  const rect = getTabRect(activeIndex)
  if (!rect) {
    indicatorStyle.value = { opacity: '0', transform: 'translate(0, 0)', width: '0px' }
    return
  }

  if (!animate || currentWidth === 0) {
    // First render or no previous position — snap directly
    setIndicator(rect.left, rect.top, rect.width, rect.height)
    currentLeft = rect.left
    currentWidth = rect.width
    return
  }

  // Phase 1: stretch to cover both old and new positions
  clearTimeout(settleTimer)
  const stretchLeft = Math.min(currentLeft, rect.left)
  const stretchRight = Math.max(currentLeft + currentWidth, rect.left + rect.width)
  setIndicator(stretchLeft, rect.top, stretchRight - stretchLeft, rect.height)

  // Phase 2: settle to target tab
  settleTimer = window.setTimeout(() => {
    setIndicator(rect.left, rect.top, rect.width, rect.height)
    currentLeft = rect.left
    currentWidth = rect.width
  }, 180)
}

watch(() => props.modelValue, async () => {
  await nextTick()
  updateIndicator(true)
})

onMounted(async () => {
  await nextTick()
  updateIndicator(false)
})
</script>

<style scoped>
.dsn-nav {
  position: fixed;
  top: var(--topbar-height, 0px);
  left: var(--sidebar-width);
  right: 0;
  z-index: 15;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 12px 2rem 12px var(--sidebar-gap);
}

/* ── Sliding pill indicator ── */
.dsn-indicator {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 8px;
  background: color-mix(in srgb, var(--apex-cyan) 8%, transparent);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              height 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.2s ease;
  pointer-events: none;
}

/* Hover */
.dsn-tab:hover:not(.dsn-tab--active) {
  background: rgba(0, 0, 0, 0.025);
}
:is(.dark *).dsn-tab:hover:not(.dsn-tab--active) {
  background: rgba(255, 255, 255, 0.035);
}

/* Active — icon + text styling (background handled by indicator) */
.dsn-tab--active {
  background: transparent;
}
.dsn-tab--active .size-4 {
  color: var(--apex-cyan);
  opacity: 1;
}
.dsn-tab--active span {
  color: var(--foreground);
  font-weight: 700;
}
</style>
